import { HTTP_STATUS } from '../domain/domainCodes.js'
/**
 * Authenticated file downloads (W4).
 *
 * The legacy panel opened export URLs in a new tab. That works while the credential is
 * a cookie the browser sends on a top-level navigation, but it gives the user nothing
 * to look at during a 30-second Excel build, and a failure arrives as a raw error page
 * in a new tab instead of a toast on the screen they were using.
 *
 * So the file is fetched like any other call, turned into a Blob and saved from memory.
 * The caller gets a promise it can put a busy state behind, and a failure is an
 * `ApiError` like everything else.
 */

import { ApiError } from './api'
import { transport } from '../components/strings'

/**
 * @param {string} url
 * @param {object} [options]
 * @param {'GET'|'POST'} [options.method]
 * @param {any} [options.body]            plain object → JSON; FormData / URLSearchParams as-is
 * @param {string} [options.filename]     overrides the server's name
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ filename: string, size: number }>}
 */
export async function download(url, { method = 'GET', body, filename, signal } = {}) {
  const init = {
    method,
    signal,
    credentials: 'same-origin',
    redirect: 'manual',
    headers: {},
  }

  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof URLSearchParams || typeof body === 'string') {
      init.body = body
    } else {
      init.headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
  }

  let response
  try {
    response = await fetch(url, init)
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    throw new ApiError(transport.network, { code: 'NETWORK' })
  }

  if (response.type === 'opaqueredirect') {
    throw new ApiError(transport.redirected, { code: 'REDIRECT' })
  }

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    throw new ApiError(transport.sessionEnded, { status: HTTP_STATUS.UNAUTHORIZED, code: 'SESSION' })
  }

  if (response.status === HTTP_STATUS.FORBIDDEN) {
    throw new ApiError(transport.deniedDownload, {
      status: HTTP_STATUS.FORBIDDEN, code: 'PROBLEM', denied: true,
    })
  }

  if (!response.ok) {
    // An export that fails usually answers JSON or text, not a file — read it so the
    // toast says why rather than "download failed".
    const text = await response.text().catch(() => '')
    throw new ApiError(messageFrom(text) ?? `Download failed (${response.status})`, {
      status: response.status,
      code: 'HTTP',
    })
  }

  const blob = await response.blob()
  const name = filename ?? filenameFrom(response.headers.get('Content-Disposition')) ?? 'download'

  save(blob, name)

  return { filename: name, size: blob.size }
}

/**
 * `Content-Disposition: attachment; filename*=UTF-8''%D8%AA%D9%82%D8%B1%D9%8A%D8%B1.xlsx`
 *
 * `filename*` is read FIRST and on purpose: an Arabic report name only survives in the
 * RFC 5987 form, and the plain `filename` beside it is the mangled ASCII fallback.
 */
function filenameFrom(header) {
  if (!header) return null

  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''))
    } catch {
      /* a malformed escape — fall through to the plain form */
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}

function messageFrom(text) {
  if (!text) return null
  try {
    const data = JSON.parse(text)
    /* A BARE JSON STRING is a message too. `Results.NotFound("File not found")` and
       `Results.BadRequest("...")` serialise the reason on its own, with no envelope around it —
       reading only the object shapes below threw that away and showed `Download failed (404)`
       instead of what the server said. */
    if (typeof data === 'string') return data || null
    return data.detail || data.Detail || data.title || data.Title || data.Message || null
  } catch {
    return text.length < 300 ? text : null
  }
}

function save(blob, filename) {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Immediately is too early in Safari; a tick is enough and the blob is small-lived.
  setTimeout(() => URL.revokeObjectURL(href), 0)
}
